import React, {useContext} from 'react';
import './Provider.css';
import {ProviderProfileContext} from "../../ProviderProfileProvider";
import {Routes} from "react-router";
import {Route} from "react-router-dom";
import {LeftNavIcon} from "./LeftNavIcon";
import {ProviderCalendar} from "./Sections/Calendar/ProviderCalendar";
import {Home} from "./Sections/Home/Home";
import {CatalogRouter} from "./Sections/Catalog/CatalogRouter";
import {ProfileRouter} from "./Sections/BusinessProfile/ProfileRouter";
import {navLinksForBusiness} from "../NavLinks";
import {TeamRouter} from "./Sections/Team/TeamRouter";
import {PublishingRouter} from "./Sections/Website/PublishingRouter";
import {ClientsRouter} from "./Sections/Clients/ClientsRouter";
import {BillingRouter} from "./Sections/Billing/BillingRouter";
import {BookingDetails} from "./Sections/Clients/ClientView/BookingDetails";
import {SettingsRouter} from "./Sections/Settings/SettingsRouter";
import {useKensuAuth} from "../../util/authwrapper/AuthWrapper";
import {LoadingDots} from "../../util/UI/LoadingDots";
import {LoginGate} from "../LoginGate";
import {isProviderEmailVerified, isProviderMobileNumberVerified} from "../../util/ProviderUtil";
import {ProviderOnboarding} from "../ProviderOnboarding/ProviderOnboarding";
import BusinessOnboarding from "../BusinessOnboarding/BusinessOnboarding";
import {ProviderHomeBanner} from "./ProviderHomeBanner";

/**
 * This is the /home page, it's the dashboard for managing your business and requires login, unlike
 * the root url which is public and shows propaganda for joining kensu.
 *
 * - If there is no user, then it asks you to login.
 *
 * - If there's no provider for the login, it asks you to inquire about become a provider.
 *
 * - If the provider (user account) onboarding is not complete, it shows that
 *
 * - If there is no business profile created yet, it shows your business onboarding survey.
 *
 * Only if all those are done then it'll show the actual dashboard.
 */
export const ProviderHome = () => {


  const {user, isLoading} = useKensuAuth();
  const {provider, profileLoading, profileError, business, businessProfile} = useContext(ProviderProfileContext);


  if (isLoading) {
    return <div>Loading<LoadingDots/></div>
  }

  if (!user) {
    return <LoginGate/>
  }

  if (profileLoading) {
    return <div>Loading business<LoadingDots/></div>
  }

  if (profileError) {
    return <div className={'ErrorText'}>Error loading your business, please try again later.</div>;
  }

  // If there's no provider the user needs to get an invite first.
  if (!provider) {
    return <div>
      We're sorry but we don't see a provider profile for this login.
      <br/><br/>
      <a style={{textDecoration: 'underline'}} href={'mailto:info@onkensu.com'}>Contact us</a> if you're interested in
      becoming a Kensu provider
    </div>
  }

  // If the team member just signed up, there is onboarding they need to go through first.
  // This collects their name, verified email, verified phone number, and requires them to
  // agree to the terms.
  if (!provider.profile?.agreedToTerms ||
      !isProviderMobileNumberVerified(provider, provider.mobileNumber) ||
      !isProviderEmailVerified(provider, provider.contactEmail)) {
    return <ProviderOnboarding/>
  }

  // Assuming the provider is set up now
  // 
  // Second step in the onboarding is the provider survey. The profile gets created
  // at the end of the survey.
  if (businessProfile == null) {
    return <BusinessOnboarding/>
  }

  // There's a business! Show the dashboard now
  const isBusinessAdmin = provider.businessRoles && provider.businessRoles.indexOf('business_admin') >= 0;

  return <div className={'ProviderRoot'}>

    {/* The left nav bar containing all the tools */}
    <div className={'LeftNav'}>
      {navLinksForBusiness(business).map((link, i) => {
        if (isBusinessAdmin || !link.requiresAdmin) {
          return <LeftNavIcon key={'nav-' + i} icon={link.icon} link={link.link} text={link.text}/>
        }
      })}
    </div>

    <div style={{flex: 1, height: '100%', display: 'flex', flexDirection: 'column'}}>

      {/* Should it be - banner 0 flex, header 0 flex, main 1 flex? */}

      <div style={{flex: 0}}>
        <ProviderHomeBanner/>
      </div>

      <Routes>
        <Route path={'/profile/*'} element={<ProfileRouter/>}/>
        <Route path={'/calendar'} element={<ProviderCalendar/>}/>
        <Route path={'/catalog/*'} element={<CatalogRouter/>}/>
        <Route path={'/clients/*'} element={<ClientsRouter/>}/>
        <Route path={'/team/*'} element={<TeamRouter/>}/>
        <Route path={'/publishing/*'} element={<PublishingRouter/>}/>
        <Route path={'/billing/*'} element={<BillingRouter/>}/>
        <Route path={'/bookings/:bookingId'} element={<BookingDetails/>}/>
        <Route path={'/settings/*'} element={<SettingsRouter/>}/>
        <Route path={'/*'} element={<Home/>}/>
      </Routes>

    </div>
  </div>
}
